import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Clock, Send, Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully!", {
        description: "We'll get back to you within 24 hours."
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Instagram,
      title: "Follow Us",
      lines: ["@laguerre.co", "DM for inquiries"],
      link: "https://instagram.com/laguerre.co"
    },
    {
      icon: Mail,
      title: "Email Us",
      lines: ["hello@laguerre.co", "frerelaguerre@gmail.com", "Response within 24 hours"]
    },
    {
      icon: Clock,
      title: "Hours",
      lines: ["Monday - Saturday: 10am - 7pm", "Sunday: By appointment"]
    },
    {
      icon: MapPin,
      title: "Location",
      lines: ["55 W Railroad Ave (Bldg 24 Suite M2)", "Garnerville, NY 10923", "By appointment only"]
    }
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">Get in Touch</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6" data-testid="contact-page-title">
              Contact Us
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto font-light">
              We'd love to hear from you. DM us on Instagram or send us an email 
              to start your custom tailoring journey with laguerre.co
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                data-testid={`contact-info-${index}`}
                className="p-8 bg-card/50 border border-white/5 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {info.link ? (
                  <a href={info.link} target="_blank" rel="noopener noreferrer" className="block hover:text-primary transition-colors">
                    <info.icon strokeWidth={1} size={28} className="text-primary mx-auto mb-4" />
                    <h3 className="font-serif text-lg mb-3">{info.title}</h3>
                    {info.lines.map((line, i) => (
                      <p key={i} className="text-sm text-muted-foreground">
                        {line}
                      </p>
                    ))}
                  </a>
                ) : (
                  <>
                    <info.icon strokeWidth={1} size={28} className="text-primary mx-auto mb-4" />
                    <h3 className="font-serif text-lg mb-3">{info.title}</h3>
                    {info.lines.map((line, i) => (
                      <p key={i} className="text-sm text-muted-foreground">
                        {line}
                      </p>
                    ))}
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="mono-text text-primary mb-4">Send a Message</p>
              <h2 className="font-serif text-3xl tracking-tight mb-8" data-testid="contact-form-title">
                We're Here to Help
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    data-testid="contact-input-name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="luxury-input"
                    required
                  />
                  <Input
                    data-testid="contact-input-email"
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="luxury-input"
                    required
                  />
                </div>

                <Input
                  data-testid="contact-input-subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="luxury-input"
                  required
                />

                <Textarea
                  data-testid="contact-input-message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="luxury-input min-h-[150px] resize-none"
                  required
                />

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="w-full md:w-auto bg-primary text-primary-foreground px-12 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground transition-all duration-300"
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send size={14} strokeWidth={1.5} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Map / Location Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-square lg:aspect-auto lg:h-full bg-card border border-white/10 relative overflow-hidden">
                {/* Decorative map placeholder */}
                <div className="absolute inset-0 opacity-30">
                  <img
                    src="https://images.unsplash.com/photo-1534430480872-3498386e7856?w=800&q=80"
                    alt="New York City"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                
                {/* Location card overlay */}
                <div className="absolute bottom-8 left-8 right-8 p-8 bg-card/90 backdrop-blur-sm border border-white/10">
                  <div className="flex items-start gap-4">
                    <MapPin strokeWidth={1.5} size={24} className="text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-serif text-xl mb-2" data-testid="location-title">Laguerre Atelier</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        55 W Railroad Ave (Bldg 24 Suite M2)<br />
                        Garnerville, NY 10923
                      </p>
                      <a
                        href="https://maps.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="get-directions-link"
                        className="text-sm text-primary hover:text-foreground transition-colors"
                      >
                        Get Directions →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-24 bg-card/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="mono-text text-primary mb-4">Common Questions</p>
            <h2 className="font-serif text-3xl tracking-tight" data-testid="faq-title">
              Frequently Asked
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                q: "How long does a custom suit take?",
                a: "A fully custom suit typically takes 4-6 weeks from initial consultation to final fitting. This allows time for pattern creation, multiple fittings, and meticulous handwork."
              },
              {
                q: "Do you offer virtual consultations?",
                a: "Yes, we offer virtual consultations for initial discussions. However, for measurements and fittings, we recommend visiting our atelier in person."
              },
              {
                q: "What fabrics do you work with?",
                a: "We source the finest fabrics from renowned mills including Loro Piana, Scabal, and Holland & Sherry. Our collection includes wools, silks, cashmeres, and luxury blends."
              },
              {
                q: "Is there a minimum order requirement?",
                a: "No, we welcome single garment orders. Whether you need one perfect suit or a complete wardrobe, we give each piece the same attention to detail."
              }
            ].map((faq, index) => (
              <motion.div
                key={faq.q}
                data-testid={`faq-item-${index}`}
                className="p-6 border border-white/5 bg-card/50"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="font-serif text-lg mb-3">{faq.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
